import { api } from './api';
import { User, LoginCredentials, RegisterCredentials } from '../types';
import { AxiosError } from 'axios';
import humps from 'humps';
import ErrorMapper from '../utils/errorMapper';
import i18n from '../i18n';

export interface AuthResponse {
    user: User;
    token?: string;
}

export interface ValidationErrors {
    [key: string]: string[];
}

/**
 * Format error response for consistent error handling
 * @param error AxiosError from the request
 * @param defaultMessage Default message if no specific error is found
 * @returns Formatted error with translated message
 */
function formatError(error: AxiosError, defaultMessage: string): Error & { validationErrors?: ValidationErrors } {
    let errorMessage = defaultMessage;
    let validationErrors: ValidationErrors | undefined;

    if (error.response?.data) {
        const responseData = error.response.data as { errors?: ValidationErrors; message?: string };

        if (responseData.errors) {
            // Use ErrorMapper to translate validation errors
            const translatedErrors = ErrorMapper.mapValidationErrors(responseData.errors, i18n.t);

            // Use translated errors for validationErrors (for form display)
            validationErrors = translatedErrors;

            // Create a flattened message for toast notifications
            const flattenedErrors = Object.values(translatedErrors)
                .flat()
                .join(', ');
            errorMessage = flattenedErrors || errorMessage;
        } else if (responseData.message) {
            // Use ErrorMapper to translate the error message
            errorMessage = ErrorMapper.mapError(responseData.message, i18n.t, 'auth:errors.login_failed');
        }
    } else {
        // Handle network errors
        errorMessage = ErrorMapper.mapError(error.message, i18n.t, 'auth:errors.network_error');
    }

    const formattedError = new Error(errorMessage) as Error & { validationErrors?: ValidationErrors };
    if (validationErrors) {
        formattedError.validationErrors = validationErrors;
    }

    return formattedError;
}

export const authService = {
    /**
     * Authenticate a user with credentials
     * @param credentials User login credentials
     * @returns AuthResponse containing user data and token
     */
    login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
        try {
            const response = await api.post('/auth/login', credentials);

            // Convert snake_case to camelCase and extract user data and token
            const data = humps.camelizeKeys(response.data) as AuthResponse;

            // Store the token in localStorage and set it in axios headers
            if (data.token) {
                api.setAuthToken(data.token);
            }

            return data;
        } catch (error) {
            console.error('[AuthService] Login error:', error);
            throw formatError(error as AxiosError, 'Failed to login. Please try again.');
        }
    },

    /**
     * Register a new user
     * @param userData User registration data
     * @returns AuthResponse containing user data and token
     */
    register: async (userData: RegisterCredentials): Promise<AuthResponse> => {
        try {
            const { confirmPassword, inviteToken, ...userDataToSend } = userData;

            const response = await api.post('/auth/register', {
                ...userDataToSend,
                password_confirmation: confirmPassword,
                invite_token: inviteToken
            });

            // Convert snake_case to camelCase
            const data = humps.camelizeKeys(response.data) as AuthResponse;

            // Store the token in localStorage and set it in axios headers
            if (data.token) {
                api.setAuthToken(data.token);
            }

            return data;
        } catch (error) {
            console.error('[AuthService] Registration error:', error);
            throw formatError(error as AxiosError, 'Failed to register. Please try again.');
        }
    },

    /**
     * Accept an invite token (for social login flow)
     */
    acceptInviteToken: async (token: string): Promise<void> => {
        try {
            await api.post('/company/invite/accept-token', { token });
        } catch (error) {
            console.error('[AuthService] Accept invite token error:', error);
            // We don't throw error here to avoid blocking login flow, but we log it
        }
    },

    /**
     * Log out the current user
     */
    logout: async (): Promise<void> => {
        try {
            await api.post('/auth/logout');

            // Remove token from localStorage and axios headers
            api.removeAuthToken();
        } catch (error) {
            console.error('[AuthService] Logout error:', error);
            // Even if the API call fails, remove the token locally
            api.removeAuthToken();
            throw formatError(error as AxiosError, 'Failed to logout. Please try again.');
        }
    },

    /**
     * Check current user's authentication status
     * @returns AuthResponse containing current user data
     */
    checkAuth: async (): Promise<AuthResponse> => {
        try {
            const response = await api.get('/auth/me');

            // Convert snake_case to camelCase and extract user data
            const user = humps.camelizeKeys(response.data) as User;
            const data: AuthResponse = { user };

            return data;
        } catch (error) {
            console.error('[AuthService] Auth check error:', error);
            throw formatError(error as AxiosError, 'Failed to check authentication status.');
        }
    },

    /**
     * Resend email verification link
     */
    resendVerificationEmail: async (): Promise<void> => {
        try {
            await api.post('/email/verification-notification');
        } catch (error) {
            console.error('[AuthService] Resend verification email error:', error);
            throw formatError(error as AxiosError, 'Failed to resend verification email.');
        }
    },

    /**
     * Verify email with signed URL parameters
     * @param params Query parameters from verification link
     * @returns Verification result
     */
    verifyEmailWithToken: async (params: {
        id: string;
        hash: string;
        expires: string;
        signature: string;
    }): Promise<{ verified: boolean; message: string }> => {
        try {
            const queryString = new URLSearchParams(params).toString();
            const response = await api.get(`/email/verify?${queryString}`);

            // Convert snake_case to camelCase
            const data = humps.camelizeKeys(response.data) as { verified: boolean; message: string };

            return data;
        } catch (error) {
            console.error('[AuthService] Email verification with token error:', error);
            throw formatError(error as AxiosError, 'Failed to verify email with provided link.');
        }
    },

    /**
     * Check email verification status
     * @returns Email verification status
     */
    checkEmailVerification: async (): Promise<{ emailVerified: boolean; email: string; verifiedAt?: string }> => {
        try {
            const response = await api.get('/email/verification-status');

            // Convert snake_case to camelCase
            const data = humps.camelizeKeys(response.data) as { verified: boolean; email: string; verifiedAt?: string };

            return {
                emailVerified: data.verified,
                email: data.email,
                verifiedAt: data.verifiedAt
            };
        } catch (error) {
            console.error('[AuthService] Email verification check error:', error);
            throw formatError(error as AxiosError, 'Failed to check email verification status.');
        }
    },

    /**
     * Get the URL for social login redirect
     * @param provider 'google' | 'github'
     * @returns URL to redirect user for OAuth
     */
    getSocialLoginUrl: (provider: 'google' | 'github'): string => {
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';
        return `${apiUrl}/auth/${provider}/redirect`;
    },

    /**
     * Handle social login callback (process token from URL)
     * @param token Token received from OAuth callback
     * @returns AuthResponse containing user data
     */
    handleSocialCallback: async (token: string): Promise<AuthResponse> => {
        // Decode token in case it was URL-encoded (Sanctum tokens may contain | character)
        const decodedToken = decodeURIComponent(token);

        console.log('[AuthService] Processing social callback token', {
            originalLength: token.length,
            decodedLength: decodedToken.length,
            tokenPreview: decodedToken.substring(0, 20) + '...'
        });

        // Store the token
        api.setAuthToken(decodedToken);

        // Verify token was stored
        const storedToken = localStorage.getItem('auth_token');
        console.log('[AuthService] Token stored:', {
            stored: !!storedToken,
            storedLength: storedToken?.length,
            matches: storedToken === decodedToken
        });

        // Small delay to ensure token is properly set before making the request
        await new Promise(resolve => setTimeout(resolve, 100));

        // Get user info
        console.log('[AuthService] Calling checkAuth...');
        try {
            const response = await authService.checkAuth();
            console.log('[AuthService] checkAuth success:', response.user?.email);
            return response;
        } catch (error) {
            console.error('[AuthService] checkAuth failed:', error);
            throw error;
        }
    },

    /**
     * Send password reset link to email
     * @param email User email
     */
    forgotPassword: async (email: string): Promise<void> => {
        try {
            await api.post('/password/email', { email });
        } catch (error) {
            console.error('[AuthService] Forgot password error:', error);
            throw formatError(error as AxiosError, 'Failed to send reset link. Please try again.');
        }
    },

    /**
     * Reset password with token
     * @param data Reset password data
     */
    resetPassword: async (data: { token: string; email: string; password: string; password_confirmation: string }): Promise<void> => {
        try {
            await api.post('/password/reset', data);
        } catch (error) {
            console.error('[AuthService] Reset password error:', error);
            throw formatError(error as AxiosError, 'Failed to reset password. Please try again.');
        }
    },
};

// Backward compat — keep class-style export as alias
export const AuthService = authService;
